/**
 * Resources Panel — 5-tab slide-in panel: Safety, Equipment, Operators, Software, Education.
 * Mirrors sidebar pattern (slide from right, glassmorphism, close on overlay/Escape).
 */
import { SAFETY_SECTIONS, EQUIPMENT_SECTIONS, MANUFACTURER_SECTIONS, SOFTWARE_SECTIONS, EDUCATION_SECTIONS } from '../data/resources-data';
import type { ResourceSection, ResourceEntry } from '../data/resources-data';
import { closeSidebar } from './sidebar';
import { isAdminMode } from '../admin/state';
import { setPanelURL, clearCountryURL } from '../url';

const RP_TABS = ['safety', 'equipment', 'manufacturers', 'software', 'education'] as const;

// ── Client-side news filter — same philosophy as the digest scraper ────────
const NEWS_DISQUALIFY = [
  'drone strike', 'air strike', 'airstrike', 'missile strike', 'military strike',
  'drone attack', 'bomb', 'bombing', 'missile', 'warhead',
  'kill', 'killed', 'killing', 'casualties', 'casualty',
  'wounded', 'shot down', 'downed', 'assassination', 'targeted killing',
  'death toll', 'civilian deaths',
  'drone war', 'drone warfare', 'warzone', 'war zone', 'battlefield',
  'frontline', 'front line', 'combat',
  'munition', 'loitering munition', 'kamikaze',
  'insurgent', 'insurgency', 'rebel forces', 'militia', 'terrorist',
  'troops', 'soldiers deployed', 'battalion', 'regiment',
  'military', 'pentagon', 'nato',
  'defense contractor', 'defence contractor',
  'armed drone', 'combat drone', 'killer drone', 'suicide drone',
  'weaponized', 'weaponised',
  'machine gun', 'weapon system', 'weapons system',
  'shahed', 'bayraktar', 'switchblade', 'reaper drone',
  'predator drone', 'global hawk', 'heron drone', 'lancet',
  'espionage', 'spy drone', 'surveillance drone',
  'counter-drone', 'counter drone', 'anti-drone',
];

function isNewsDisqualified(title: string, description: string): boolean {
  const text = `${title} ${description}`.toLowerCase();
  return NEWS_DISQUALIFY.some(phrase => text.includes(phrase));
}
type RPTab = typeof RP_TABS[number];

let activeTab: RPTab = 'safety';

/* ── Lifecycle ──────────────────────────────────────────────────────────── */

export function initResourcesPanel(onClose?: () => void): void {
  const overlay = document.getElementById('resources-overlay')!;
  overlay.addEventListener('click', () => closeResourcesPanel(onClose));
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      const panel = document.getElementById('resources-panel');
      if (panel?.classList.contains('open')) closeResourcesPanel(onClose);
    }
  });
}

export function openResourcesPanel(tab?: RPTab): void {
  // Close country sidebar first
  closeSidebar();

  activeTab = tab || 'safety';
  renderPanel();

  const panel = document.getElementById('resources-panel')!;
  const overlay = document.getElementById('resources-overlay')!;
  panel.classList.add('open');
  overlay.classList.add('visible');
  setPanelURL(activeTab);
}

export function closeResourcesPanel(onClose?: () => void): void {
  const panel = document.getElementById('resources-panel')!;
  const overlay = document.getElementById('resources-overlay')!;
  panel.classList.remove('open');
  overlay.classList.remove('visible');
  clearCountryURL();
  onClose?.();
}

export function isResourcesPanelOpen(): boolean {
  return document.getElementById('resources-panel')?.classList.contains('open') ?? false;
}

/* ── Helpers ─────────────────────────────────────────────────────────────── */

function getTabSections(tabId: string): ResourceSection[] | null {
  switch (tabId) {
    case 'safety': return SAFETY_SECTIONS;
    case 'equipment': return EQUIPMENT_SECTIONS;
    case 'manufacturers': return MANUFACTURER_SECTIONS;
    case 'software': return SOFTWARE_SECTIONS;
    case 'education': return EDUCATION_SECTIONS;
    default: return null;
  }
}

/* ── Render ──────────────────────────────────────────────────────────────── */

function renderPanel(): void {
  const panel = document.getElementById('resources-panel')!;

  panel.innerHTML = `
    <div class="rp-header">
      <div class="rp-title-area">
        <span class="rp-title-icon">📚</span>
        <h2 class="rp-title">Resources</h2>
      </div>
      <button class="rp-close" aria-label="Close resources">&times;</button>
    </div>
    <nav class="rp-tabs" role="tablist">
      <button class="rp-tab${activeTab === 'safety' ? ' active' : ''}" data-rp-tab="safety" role="tab" aria-selected="${activeTab === 'safety' ? 'true' : 'false'}"><span class="rp-tab-emoji">🛡️</span><span class="rp-tab-label">Safety</span></button>
      <button class="rp-tab${activeTab === 'equipment' ? ' active' : ''}" data-rp-tab="equipment" role="tab" aria-selected="${activeTab === 'equipment' ? 'true' : 'false'}"><span class="rp-tab-emoji">🏭</span><span class="rp-tab-label">Equipment</span></button>
      <button class="rp-tab${activeTab === 'manufacturers' ? ' active' : ''}" data-rp-tab="manufacturers" role="tab" aria-selected="${activeTab === 'manufacturers' ? 'true' : 'false'}"><span class="rp-tab-emoji">🌍</span><span class="rp-tab-label">Operators</span></button>
      <span class="rp-tab-break"></span>
      <button class="rp-tab${activeTab === 'software' ? ' active' : ''}" data-rp-tab="software" role="tab" aria-selected="${activeTab === 'software' ? 'true' : 'false'}"><span class="rp-tab-emoji">💻</span><span class="rp-tab-label">Software</span></button>
      <button class="rp-tab${activeTab === 'education' ? ' active' : ''}" data-rp-tab="education" role="tab" aria-selected="${activeTab === 'education' ? 'true' : 'false'}"><span class="rp-tab-emoji">🎓</span><span class="rp-tab-label">Education</span></button>
    </nav>
    <div class="rp-body">
      <div class="rp-panel${activeTab === 'safety' ? ' active' : ''}" data-rp-panel="safety">
        ${renderAccordionSections(SAFETY_SECTIONS, 'safety')}
      </div>
      <div class="rp-panel${activeTab === 'equipment' ? ' active' : ''}" data-rp-panel="equipment">
        ${renderAccordionSections(EQUIPMENT_SECTIONS, 'equipment')}
      </div>
      <div class="rp-panel${activeTab === 'manufacturers' ? ' active' : ''}" data-rp-panel="manufacturers">
        ${renderAccordionSections(MANUFACTURER_SECTIONS, 'manufacturers')}
      </div>
      <div class="rp-panel${activeTab === 'software' ? ' active' : ''}" data-rp-panel="software">
        ${renderAccordionSections(SOFTWARE_SECTIONS, 'software')}
      </div>
      <div class="rp-panel${activeTab === 'education' ? ' active' : ''}" data-rp-panel="education">
        ${renderAccordionSections(EDUCATION_SECTIONS, 'education')}
      </div>
    </div>
    <div class="rp-join-cta">
      <p class="pilot-cta-text">Are you a drone service provider? <strong>Join our network.</strong></p>
      <a href="https://africandrone.org/contact-us" target="_blank" rel="noopener" class="pilot-cta-btn">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="15" height="15"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
        Join our network of drone service providers
      </a>
    </div>
  `;

  wirePanel(panel);
}

function renderAccordionSections(sections: ResourceSection[], tabId: string): string {
  const editBtn = isAdminMode()
    ? `<button class="sidebar-edit-btn rp-edit-btn" data-edit-tab="${tabId}">Edit</button>`
    : '';
  return `
    <div class="rp-tab-toolbar">
      <input class="rp-search" type="text" placeholder="Search..." data-search-tab="${tabId}" />
      ${editBtn}
    </div>
    ${sections.map(section => `
      <div class="rp-section" data-section="${section.id}">
        <button class="rp-section-header" type="button" aria-expanded="false">
          <span class="rp-section-icon">${section.icon}</span>
          <span class="rp-section-label">${section.label}</span>
          <span class="rp-section-count">${section.entries.length}</span>
          <span class="rp-chevron">▸</span>
        </button>
        <div class="rp-section-body">
          ${section.entries.map(entry => renderEntry(entry)).join('')}
        </div>
      </div>
    `).join('')}
  `;
}

function renderEntry(entry: ResourceEntry): string {
  const flag = entry.hqCountry ? countryFlag(entry.hqCountry) : '';
  return `
    <a class="rp-entry" href="${entry.url}" target="_blank" rel="noopener" data-name="${entry.name.toLowerCase()}">
      <span class="rp-entry-name">${entry.name}</span>
      ${entry.description ? `<span class="rp-entry-desc">${entry.description}</span>` : ''}
      <span class="rp-entry-meta">
        ${flag ? `<span class="rp-entry-flag">${flag}</span>` : ''}
        <span class="rp-entry-arrow">→</span>
      </span>
    </a>
  `;
}

/* ── Wire Events ────────────────────────────────────────────────────────── */

function wirePanel(panel: HTMLElement): void {
  // Close button
  panel.querySelector('.rp-close')!.addEventListener('click', () => closeResourcesPanel());

  // Tab switching
  panel.querySelectorAll('.rp-tab').forEach(btn => {
    btn.addEventListener('click', () => {
      activeTab = (btn as HTMLElement).dataset.rpTab as RPTab;
      // Toggle active classes
      panel.querySelectorAll('.rp-tab').forEach(t => {
        const isActive = (t as HTMLElement).dataset.rpTab === activeTab;
        t.classList.toggle('active', isActive);
        t.setAttribute('aria-selected', isActive ? 'true' : 'false');
      });
      panel.querySelectorAll('.rp-panel').forEach(p => p.classList.toggle('active', (p as HTMLElement).dataset.rpPanel === activeTab));
      setPanelURL(activeTab);
    });
  });

  // Accordion toggle
  panel.querySelectorAll('.rp-section-header').forEach(btn => {
    btn.addEventListener('click', () => {
      const section = btn.closest('.rp-section')!;
      const wasOpen = section.classList.contains('open');
      // Close all siblings (optional: remove this to allow multiple)
      section.parentElement?.querySelectorAll('.rp-section.open').forEach(s => {
        if (s !== section) {
          s.classList.remove('open');
          s.querySelector('.rp-section-header')?.setAttribute('aria-expanded', 'false');
        }
      });
      section.classList.toggle('open', !wasOpen);
      btn.setAttribute('aria-expanded', !wasOpen ? 'true' : 'false');
    });
  });

  // Search filtering
  panel.querySelectorAll('.rp-search').forEach(input => {
    input.addEventListener('input', () => {
      const query = (input as HTMLInputElement).value.toLowerCase().trim();
      const tabId = (input as HTMLInputElement).dataset.searchTab;
      const tabPanel = panel.querySelector(`[data-rp-panel="${tabId}"]`);
      if (!tabPanel) return;

      tabPanel.querySelectorAll('.rp-section').forEach(section => {
        let visibleCount = 0;
        section.querySelectorAll('.rp-entry').forEach(entry => {
          const name = (entry as HTMLElement).dataset.name || '';
          const desc = entry.querySelector('.rp-entry-desc')?.textContent?.toLowerCase() || '';
          const matches = !query || name.includes(query) || desc.includes(query);
          (entry as HTMLElement).style.display = matches ? '' : 'none';
          if (matches) visibleCount++;
        });
        // Show/hide section based on whether it has visible entries
        (section as HTMLElement).style.display = visibleCount > 0 || !query ? '' : 'none';
        // Update count
        const countEl = section.querySelector('.rp-section-count');
        if (countEl) countEl.textContent = query ? `${visibleCount}` : `${section.querySelectorAll('.rp-entry').length}`;
        // Auto-open sections with matches when searching
        const header = section.querySelector('.rp-section-header');
        if (query && visibleCount > 0) {
          section.classList.add('open');
          header?.setAttribute('aria-expanded', 'true');
        }
        if (!query) {
          section.classList.remove('open');
          header?.setAttribute('aria-expanded', 'false');
        }
      });
    });
  });

  // Admin: Edit buttons for resource tabs
  panel.querySelectorAll('.rp-edit-btn').forEach(btn => {
    btn.addEventListener('click', async () => {
      const tabId = (btn as HTMLElement).dataset.editTab!;
      const sections = getTabSections(tabId);
      if (!sections) return;
      const { renderResourceEditMode } = await import('../admin/resources-editor');
      renderResourceEditMode(tabId, sections, panel, () => renderPanel());
    });
  });

}

/* ── Helpers ─────────────────────────────────────────────────────────────── */

function countryFlag(iso2: string): string {
  try {
    return String.fromCodePoint(
      ...iso2.toUpperCase().split('').map(c => 0x1F1E6 + c.charCodeAt(0) - 65)
    );
  } catch {
    return '';
  }
}
